import {Box} from '@primer/react'
import {Suspense} from 'react'
import {useIssueCreateDataContext} from './../contexts/IssueCreateDataContext'
import {useIssueCreateConfigContext} from './../contexts/IssueCreateConfigContext'
import {CreateIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {CreateIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {CreateIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'
import {CreateIssueProjectsSection} from '@github-ui/issue-metadata/ProjectsSection'
import type {SharedMetadataProps} from './MetadataSelectors'
import {MetadataSidebarLoading} from './MetadataSidebarLoading'

export const MetadataSidebar = ({
  viewerRef,
  projectsRef,
  setMilestoneCallback,
  canAssign,
  canLabel,
  canMilestone,
  canProject,
}: SharedMetadataProps) => {
  const {optionConfig} = useIssueCreateConfigContext()
  const {repository, labels, setLabels, assignees, setAssignees, projects, setProjects, milestone} =
    useIssueCreateDataContext()

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
    shortcutEnabled: optionConfig.singleKeyShortcutsEnabled,
    shortcutHintVisible: false,
  }

  if (!repository) {
    return null
  }

  // This is to avoid flickering on hard navigation as SSR won't load anything, given that
  // we load the ref query data on the client side
  if (!viewerRef && !projectsRef) {
    return <MetadataSidebarLoading />
  }

  return (
    <Suspense fallback={<MetadataSidebarLoading />}>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, flexWrap: 'wrap'}}>
        <h2 className="sr-only">Metadata</h2>
        {repository && viewerRef && (
          <CreateIssueAssigneesSection
            sx={{flexGrow: 1}}
            viewerRef={viewerRef}
            repoName={repository.name}
            repoOwner={repository.owner.login}
            readonly={!canAssign}
            assignees={assignees}
            onSelectionChange={setAssignees}
            {...sharedConfigProps}
          />
        )}
        <CreateIssueLabelsSection
          repo={repository.name}
          owner={repository.owner.login}
          readonly={!canLabel}
          labels={labels}
          onSelectionChange={setLabels}
          {...sharedConfigProps}
        />
        {projectsRef && (
          <CreateIssueProjectsSection
            owner={repository.owner.login}
            repo={repository.name}
            projects={projects}
            readonly={!canProject}
            onSelectionChange={setProjects}
            projectQueryRef={projectsRef}
            {...sharedConfigProps}
          />
        )}
        <CreateIssueMilestonesSection
          sx={{flexGrow: 1}}
          repo={repository.name}
          owner={repository.owner.login}
          activeMilestone={milestone}
          onSelectionChange={setMilestoneCallback}
          viewerCanSetMilestone={canMilestone ?? false}
          {...sharedConfigProps}
        />
      </Box>
    </Suspense>
  )
}

try{ MetadataSidebar.displayName ||= 'MetadataSidebar' } catch {}